import type { AgentEstateGroup } from "@/api/temp/AgentEstateService";
import type { GuardianContextKind } from "@/api/temp/TrustAIExecutiveService";

export type { GuardianContextKind };

/**
 * What Guardian is currently reasoning about.
 *
 * `key` is stable per subject and is both the script lookup and the identity
 * check that suppresses a duplicate "now discussing" marker when the same
 * subject is selected twice. `label` is already translated by the call site —
 * the mock never translates.
 */
export interface GuardianContext {
  kind: GuardianContextKind;
  key: string;
  label: string;
  metadata?: Record<string, unknown>;
}

/**
 * Explore's three lenses are three of AgentEstate's group dimensions, so the
 * mapping back to a Guardian kind lives beside the context builders rather
 * than being re-derived wherever a card is rendered.
 */
const DIMENSION_CONTEXT_KIND: Record<string, GuardianContextKind> = {
  BUSINESS_UNIT: "business-function",
  PLATFORM: "ai-platform",
  CONNECTED_RESOURCE: "data-platform",
};

/**
 * The data-source figures Guardian may quote for a data platform. Carried in
 * the context because the reply is composed away from the page: passing the
 * tenant's own numbers is what keeps "N data sources, M requiring attention" in
 * step with the Data Sources tab, instead of restating a different fixture.
 * `accessingAgentCount` is only present when the caller actually knows it.
 */
export interface GuardianDataPlatform {
  id: string;
  name: string;
  dataSourceTotal: number;
  incompleteCount: number;
  accessingAgentCount?: number;
}

/** The whole estate — the default subject when Guardian is opened cold. */
export const estateContext = (label: string): GuardianContext => ({
  kind: "estate",
  key: "estate",
  label,
});

/**
 * The whole data estate. Its own subject, not a platform within it: the store
 * matches `data-estate` before any `data-platform:<id>` key.
 */
export const dataEstateContext = (
  label: string,
  dataPlatforms?: readonly GuardianDataPlatform[],
): GuardianContext => ({
  kind: "data-platform",
  key: "data-estate",
  label,
  ...(dataPlatforms ? { metadata: { dataPlatforms } } : {}),
});

/**
 * One filtered agent list. The filters are the subject, so they are the key —
 * serialized here rather than at the call site so every list that scopes
 * Guardian produces the same key for the same filters.
 */
export const agentListContext = (
  filters: object,
  label: string,
  totalAgents: number,
): GuardianContext => ({
  kind: "estate",
  key: `agents:${JSON.stringify(filters)}`,
  label,
  metadata: { filters, totalAgents },
});

/**
 * One Explore category. Reuses `group.key`, which the estate store already
 * emits as `${dimension}:${value}` — no second id scheme to keep in step.
 */
export const groupContext = (
  group: AgentEstateGroup,
  label: string,
): GuardianContext => ({
  kind: DIMENSION_CONTEXT_KIND[group.dimension] ?? "business-function",
  key: group.key,
  label,
  metadata: {
    totalAgents: group.totalAgents,
    secureCount: group.secureCount,
    notSecureCount: group.notSecureCount,
    notEvaluatedCount: group.notEvaluatedCount,
  },
});

/**
 * One data platform's governance. Reuses the existing `data-platform` kind —
 * Guardian already speaks about data platforms; what is new is the key, which
 * scopes the script to a specific platform rather than the estate's view of
 * that technology.
 */
export const dataPlatformContext = (
  id: string,
  name: string,
  platform?: Pick<
    GuardianDataPlatform,
    "dataSourceTotal" | "incompleteCount" | "accessingAgentCount"
  >,
): GuardianContext => ({
  kind: "data-platform",
  key: `data-platform:${id}`,
  label: name,
  ...(platform
    ? { metadata: { dataPlatforms: [{ id, name, ...platform }] } }
    : {}),
});

export const agentContext = (id: string, name: string): GuardianContext => ({
  kind: "agent",
  key: `agent:${id}`,
  label: name,
});

export const riskContext = (issue: {
  id: string;
  name: string;
}): GuardianContext => ({
  kind: "risk",
  key: `risk:${issue.id}`,
  label: issue.name,
});

/**
 * A policy recommendation. Takes primitives rather than a
 * `GuardianRecommendation`, because the same context is reached from an issue
 * card (whose recommendation is a line on the control) and from
 * Guardian's own recommendation list.
 */
export const recommendationContext = (
  id: string,
  label: string,
): GuardianContext => ({
  kind: "recommendation",
  key: `recommendation:${id}`,
  label,
});

/** Labels and metadata are not resource identity; `key` already carries it. */
export const sameGuardianContext = (
  left: GuardianContext,
  right: GuardianContext,
) => left.kind === right.kind && left.key === right.key;
